import { useState, useEffect, useContext, useRef } from "react";
import { Modal, IconButton, Toggle } from "@fluentui/react";
import { Brain, User, Shield, Palette, Bell, Moon, Sun, Monitor, ChevronDown, Building2, Camera, X, Check, Pencil, ShieldCheck, ShieldAlert, ChevronRight, Sparkles, Workflow } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getMemorySettings, updateMemorySettings, MemorySettings } from "../../api/memoryApi";
import { getToken, useMsalAuth } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { LoginContext } from "../../loginContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useCompany } from "../../contexts/CompanyContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import {
    UserProfile,
    UserProfileUpdateData,
    updateUserProfileApi,
    uploadProfilePhotoApi,
    getCompanySharedDocuments,
    uploadCompanySharedDocument,
    deleteCompanySharedDocument,
    updateCompanyProfileApi,
    getCompanyWorkRulesApi,
    updateCompanyWorkRulesApi,
    extractPlanningRulesApi,
    addCompanyMemberApi,
    updateCompanyMemberRoleApi
} from "../../api/api";
import { clearCurrentUserCache, getCurrentUser } from "../../api/currentUser";
import { CompanyMember, CompanySharedDocument, WorkRules } from "../../api/models";
import { OrgStructureEditor } from "../CompanyOrgStructure";
import { NoteWorkflowsEditor } from "../NoteWorkflows";
import { AssessmentsEditor } from "../CompanyAssessments";
import { FormTemplateManager } from "../FormTemplates/FormTemplateManager";
import { DocumentationDefinitionAdmin } from "../../features/documentation/DocumentationDefinitionAdmin";
import { WorkflowBuilderWindow } from "../../features/workflowBuilder/WorkflowBuilderWindow";
import styles from "./UserSettings.module.css";
import { ReportThemeSettings } from "./ReportThemeSettings";

// Dropdown options
const DEPARTMENT_OPTIONS = ["HVB", "LSS", "Elderly Care", "Personal Assistance", "Home Healthcare", "Other"];
const LANGUAGE_OPTIONS = ["Swedish", "English", "German", "Italian", "Spanish", "French", "Arabic", "Polish", "Finnish", "Norwegian", "Danish"];
const SKILLS_OPTIONS = ["Medication delegation", "Driver's license", "Night shift", "Double staffing", "Trainings"];
const EMPLOYMENT_TYPE_OPTIONS = [
    { value: "hourly", label: "Hourly" },
    { value: "part-time", label: "Part-time" },
    { value: "full-time", label: "Full-time" }
];

interface UserSettingsProps {
    isOpen: boolean;
    onDismiss: () => void;
}

type SettingsTab = "general" | "personalization" | "account" | "privacy" | "company";

const COMPANY_PROFILE_MAX_LEN = 5000;

// Default WorkRules; mirrors backend defaults so the form always renders
// a complete shape even before the first GET resolves.
const DEFAULT_WORK_RULES: WorkRules = {
    rest_between_shifts_hours: 11,
    rest_between_shifts_enabled: true,
    min_shift_duration_for_rest_rule_hours: 3,
    max_continuous_work_hours: 5,
    max_continuous_work_enabled: true,
    min_weekly_continuous_rest_hours: 36,
    weekly_continuous_rest_enabled: true,
    max_weekly_hours: null,
    max_monthly_overtime_hours: null,
    max_consecutive_work_days: null,
    max_night_shift_hours: null,
    work_hours: { default_start: "07:00", default_end: "23:00" },
    legal_rules_source: "",
    soft_preferences: "",
    unsupported_rules: [],
    employee_preferences: [],
    client_continuity: false
};

// Static transparency manifest: which rules the validator currently enforces
// vs. which are recognized but not yet checked. Keep in sync with
// app/backend/planning/validator.py — when validator gains a new check,
// move the row from "later" to "v1".
type RuleSupport = "v1" | "later";
type RuleTier = "legal" | "policy" | "preference";

interface SupportedRuleRow {
    key: string;          // i18n key under settingsPage.company.rules
    tier: RuleTier;
    support: RuleSupport;
}

const SUPPORTED_RULES: SupportedRuleRow[] = [
    { key: "restBetweenShifts", tier: "legal", support: "v1" },
    { key: "weeklyRest", tier: "legal", support: "v1" },
    { key: "continuousWorkBreak", tier: "policy", support: "v1" },
    { key: "weeklyHoursCap", tier: "legal", support: "v1" },
    { key: "qualificationMatch", tier: "policy", support: "v1" },
    { key: "compensatoryRest", tier: "legal", support: "later" },
    { key: "monthlyOvertime", tier: "legal", support: "later" },
    { key: "nightShiftLimit", tier: "legal", support: "later" },
    { key: "consecutiveDays", tier: "policy", support: "later" },
    { key: "coverageMinimums", tier: "policy", support: "later" },
    { key: "managerOverride", tier: "policy", support: "later" }
];

export const UserSettings = ({ isOpen, onDismiss }: UserSettingsProps) => {
    const [workflowWindowOpen, setWorkflowWindowOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<SettingsTab>("general");
    const [settings, setSettings] = useState<MemorySettings | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
    const [userCompanyRole, setUserCompanyRole] = useState<string | null>(null);

    // Profile state
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [editingProfile, setEditingProfile] = useState(false);
    const [profileDraft, setProfileDraft] = useState<UserProfileUpdateData>({});
    const [savingProfile, setSavingProfile] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const sharedDocInputRef = useRef<HTMLInputElement>(null);
    const [companySharedDocuments, setCompanySharedDocuments] = useState<CompanySharedDocument[]>([]);
    const [companyDocsLoading, setCompanyDocsLoading] = useState(false);
    const [companyDocsError, setCompanyDocsError] = useState<string | null>(null);
    const [uploadingCompanyDoc, setUploadingCompanyDoc] = useState(false);
    const [deletingCompanyDoc, setDeletingCompanyDoc] = useState<string | null>(null);
    const [noteWorkflowNodeDialogOpen, setNoteWorkflowNodeDialogOpen] = useState(false);
    // Advanced: documentation types are normally derived automatically from the
    // Note Workflows above; opt in via ?advancedDocTypes=1 to edit them directly.
    const showAdvancedDocumentationTypes = typeof window !== "undefined" && window.location.href.includes("advancedDocTypes=1");
    const [orgStructureNodeDialogOpen, setOrgStructureNodeDialogOpen] = useState(false);
    const [assessmentsDialogOpen, setAssessmentsDialogOpen] = useState(false);

    const { loggedIn, userInfo, setUserInfo } = useContext(LoginContext);
    const client = useMsalAuth ? useMsal().instance : undefined;
    const { theme, setTheme } = useTheme();
    const { company, loading: companyLoading, refreshCompany, companyRole, roleReady, members, refreshMembers } = useCompany();
    const { isOrganizationAdmin } = useOrganization();
    const { t } = useTranslation();

    useEffect(() => {
        if (!isOpen) {
            setNoteWorkflowNodeDialogOpen(false);
            setWorkflowWindowOpen(false);
        }
    }, [isOpen]);

    // Company profile (free-text) state
    const [editingCompanyProfile, setEditingCompanyProfile] = useState(false);
    const [companyProfileDraft, setCompanyProfileDraft] = useState("");
    const [savingCompanyProfile, setSavingCompanyProfile] = useState(false);
    const [companyProfileError, setCompanyProfileError] = useState<string | null>(null);

    // Company members management state
    const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
    const [newMemberEmail, setNewMemberEmail] = useState("");
    const [newMemberRole, setNewMemberRole] = useState<"admin" | "member">("member");
    const [addingMember, setAddingMember] = useState(false);
    const [updatingMemberEmail, setUpdatingMemberEmail] = useState<string | null>(null);
    const [membersError, setMembersError] = useState<string | null>(null);

    // Scheduling rules (structured) state
    const [workRules, setWorkRules] = useState<WorkRules | null>(null);
    const [workRulesLoading, setWorkRulesLoading] = useState(false);
    const [editingWorkRules, setEditingWorkRules] = useState(false);
    const [workRulesDraft, setWorkRulesDraft] = useState<WorkRules>(DEFAULT_WORK_RULES);
    const [savingWorkRules, setSavingWorkRules] = useState(false);
    const [workRulesError, setWorkRulesError] = useState<string | null>(null);
    // Free-text policy textarea + LLM parse
    const [rulesSourceText, setRulesSourceText] = useState("");
    const [parsing, setParsing] = useState(false);
    const [parseError, setParseError] = useState<string | null>(null);

    const getIdToken = async () => {
        if (client && loggedIn) {
            try {
                return await getToken(client);
            } catch (error) {
                console.error("Failed to get auth token:", error);
                return undefined;
            }
        }
        return undefined;
    };

    // Load settings when modal opens
    useEffect(() => {
        if (isOpen) {
            loadSettings();
            loadUserCompanyRole();
        }
    }, [isOpen, isOrganizationAdmin]);

    const loadCompanySharedDocuments = async (idTokenOverride?: string) => {
        try {
            setCompanyDocsLoading(true);
            setCompanyDocsError(null);
            const idToken = idTokenOverride ?? (await getIdToken());
            const docs = await getCompanySharedDocuments(idToken);
            setCompanySharedDocuments(docs);
        } catch (err: any) {
            console.error("Failed to load company shared documents:", err);
            setCompanyDocsError(err.message || t("settingsPage.errors.sharedDocsLoadFailed"));
        } finally {
            setCompanyDocsLoading(false);
        }
    };

    const loadUserCompanyRole = async () => {
        try {
            setProfileLoading(true);
            const idToken = await getIdToken();
            const response = await getCurrentUser({ idToken });

            if (response.ok) {
                const userData = response.user;
                const role = userData?.company_role || null;
                setUserCompanyRole(role);
                setCurrentUserEmail(userData?.email || null);

                // Load profile data
                if (userData?.profile) {
                    setProfile(userData.profile as unknown as UserProfile);
                }

                if (role === "admin" || isOrganizationAdmin) {
                    await loadCompanySharedDocuments(idToken);
                    await loadWorkRules(idToken);
                    await refreshMembers();
                } else {
                    setCompanySharedDocuments([]);
                    setCompanyDocsError(null);
                }
            }
        } catch (err) {
            console.error("Failed to load user data:", err);
            setProfileError(t("settingsPage.errors.loadFailed"));
        } finally {
            setProfileLoading(false);
        }
    };

    const handleCompanyDocUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadingCompanyDoc(true);
            setCompanyDocsError(null);
            const idToken = await getIdToken();
            await uploadCompanySharedDocument(file, idToken);
            await loadCompanySharedDocuments(idToken);
        } catch (err: any) {
            console.error("Failed to upload company shared document:", err);
            setCompanyDocsError(err.message || t("settingsPage.errors.sharedDocsUploadFailed"));
        } finally {
            setUploadingCompanyDoc(false);
            if (event.target) {
                event.target.value = "";
            }
        }
    };

    const handleCompanyDocDelete = async (filename: string) => {
        try {
            setDeletingCompanyDoc(filename);
            setCompanyDocsError(null);
            const idToken = await getIdToken();
            await deleteCompanySharedDocument(filename, idToken);
            setCompanySharedDocuments(prev => prev.filter(doc => doc.filename !== filename));
        } catch (err: any) {
            console.error("Failed to delete company shared document:", err);
            setCompanyDocsError(err.message || t("settingsPage.errors.sharedDocsDeleteFailed"));
        } finally {
            setDeletingCompanyDoc(null);
        }
    };

    const formatDocumentSize = (size?: number): string => {
        if (!size || size <= 0) return "-";
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatUploadedAt = (timestamp?: string): string => {
        if (!timestamp) return "-";
        const parsed = new Date(timestamp);
        if (Number.isNaN(parsed.getTime())) return "-";
        return parsed.toLocaleString();
    };

    const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadingPhoto(true);
            setProfileError(null);
            const idToken = await getIdToken();
            const result = await uploadProfilePhotoApi(file, idToken);
            clearCurrentUserCache();

            // Update profile with new photo URL
            setProfile(prev => (prev ? { ...prev, profile_photo_url: result.profile_photo_url } : null));

            // Update userInfo context to reflect in sidebar
            if (setUserInfo) {
                setUserInfo(prev => ({ ...prev, photoUrl: result.profile_photo_url }));
            }
        } catch (err: any) {
            console.error("Failed to upload photo:", err);
            setProfileError(err.message || t("settingsPage.errors.uploadFailed"));
        } finally {
            setUploadingPhoto(false);
        }
    };

    const startEditingProfile = () => {
        setEditingProfile(true);
        setProfileDraft({
            first_name: profile?.first_name || "",
            last_name: profile?.last_name || "",
            phone: profile?.phone || "",
            department: profile?.department || [],
            languages: profile?.languages || [],
            skills: profile?.skills || []
        });
    };

    const cancelEditingProfile = () => {
        setEditingProfile(false);
        setProfileDraft({});
        setProfileError(null);
    };

    const saveProfile = async () => {
        try {
            setSavingProfile(true);
            setProfileError(null);
            const idToken = await getIdToken();
            const result = await updateUserProfileApi(profileDraft, idToken);
            clearCurrentUserCache();

            setProfile(result.profile);
            setEditingProfile(false);

            // Update userInfo context if name fields changed
            if (setUserInfo) {
                const composedName = `${result.profile.first_name || ""} ${result.profile.last_name || ""}`.trim();
                setUserInfo(prev => ({ ...prev, name: composedName || prev.name }));
            }
            setProfileDraft({});
        } catch (err: any) {
            console.error("Failed to save profile:", err);
            setProfileError(err.message || t("settingsPage.errors.saveFailed"));
        } finally {
            setSavingProfile(false);
        }
    };

    const startEditingCompanyProfile = () => {
        setCompanyProfileDraft(company?.profile_description ?? "");
        setCompanyProfileError(null);
        setEditingCompanyProfile(true);
    };

    const cancelEditingCompanyProfile = () => {
        setEditingCompanyProfile(false);
        setCompanyProfileDraft("");
        setCompanyProfileError(null);
    };

    const saveCompanyProfile = async () => {
        try {
            setSavingCompanyProfile(true);
            setCompanyProfileError(null);
            const idToken = await getIdToken();
            await updateCompanyProfileApi(companyProfileDraft, idToken);
            await refreshCompany();
            setEditingCompanyProfile(false);
            setCompanyProfileDraft("");
        } catch (err: any) {
            console.error("Failed to save company profile:", err);
            setCompanyProfileError(err?.message || t("settingsPage.errors.saveFailed"));
        } finally {
            setSavingCompanyProfile(false);
        }
    };

    const formatCompanyProfileUpdated = (timestamp?: number): string => {
        if (!timestamp) return "";
        const date = new Date(timestamp * 1000);
        if (Number.isNaN(date.getTime())) return "";
        return date.toLocaleString();
    };

    // ----- Company members handlers -----

    const handleAddMember = async () => {
        const email = newMemberEmail.trim();
        if (!email) return;
        try {
            setAddingMember(true);
            setMembersError(null);
            const idToken = await getIdToken();
            await addCompanyMemberApi(email, newMemberRole, idToken);
            setNewMemberEmail("");
            setNewMemberRole("member");
            await refreshMembers();
        } catch (err: any) {
            console.error("Failed to add company member:", err);
            setMembersError(err?.message || t("settingsPage.errors.saveFailed"));
        } finally {
            setAddingMember(false);
        }
    };

    const handleMemberRoleChange = async (member: CompanyMember, role: "admin" | "member") => {
        if (role === member.role) return;
        try {
            setUpdatingMemberEmail(member.email);
            setMembersError(null);
            const idToken = await getIdToken();
            await updateCompanyMemberRoleApi(member.id, role, idToken);
            await refreshMembers();
        } catch (err: any) {
            console.error("Failed to update member role:", err);
            setMembersError(err?.message || t("settingsPage.errors.saveFailed"));
        } finally {
            setUpdatingMemberEmail(null);
        }
    };

    // ----- Scheduling rules handlers -----

    const loadWorkRules = async (idTokenOverride?: string) => {
        try {
            setWorkRulesLoading(true);
            setWorkRulesError(null);
            const idToken = idTokenOverride ?? (await getIdToken());
            const fetched = await getCompanyWorkRulesApi(idToken);
            setWorkRules(fetched);
        } catch (err: any) {
            console.error("Failed to load work rules:", err);
            setWorkRulesError(err?.message || t("settingsPage.errors.loadFailed"));
        } finally {
            setWorkRulesLoading(false);
        }
    };

    const startEditingWorkRules = () => {
        const base = workRules ?? DEFAULT_WORK_RULES;
        setWorkRulesDraft(base);
        // Seed the textarea with whatever policy text was saved last time so
        // the admin can tweak it instead of retyping.
        setRulesSourceText(base.legal_rules_source || "");
        setParseError(null);
        setWorkRulesError(null);
        setEditingWorkRules(true);
    };

    const cancelEditingWorkRules = () => {
        setEditingWorkRules(false);
        setWorkRulesDraft(workRules ?? DEFAULT_WORK_RULES);
        setRulesSourceText("");
        setParseError(null);
        setWorkRulesError(null);
    };

    const saveWorkRules = async () => {
        try {
            setSavingWorkRules(true);
            setWorkRulesError(null);
            const idToken = await getIdToken();
            // Persist the raw policy text alongside the structured rules so
            // it round-trips on the next edit.
            const payload: WorkRules = {
                ...workRulesDraft,
                legal_rules_source: rulesSourceText.trim()
            };
            const saved = await updateCompanyWorkRulesApi(payload, idToken);
            setWorkRules(saved);
            setEditingWorkRules(false);
        } catch (err: any) {
            console.error("Failed to save work rules:", err);
            setWorkRulesError(err?.message || t("settingsPage.errors.saveFailed"));
        } finally {
            setSavingWorkRules(false);
        }
    };

    // Parse the free-text policy with the LLM and merge the result into the
    // draft. Critically: this is an ASSIST, not a gate. On failure the
    // textarea text is preserved and the structured form stays usable so
    // the admin can hand-adjust without the LLM.
    const parseRulesText = async () => {
        const text = rulesSourceText.trim();
        if (!text) return;
        try {
            setParsing(true);
            setParseError(null);
            const idToken = await getIdToken();
            const result = await extractPlanningRulesApi(
                { filename: "scheduling-policy.txt", content: text },
                idToken
            );
            if (result.extract_failed || !result.work_rules) {
                setParseError(t("settingsPage.company.schedulingRules.parseFailed"));
                return;
            }
            // Overlay parsed values onto the current draft; keep the admin's
            // textarea text. They review/adjust, then Save.
            setWorkRulesDraft(prev => ({
                ...prev,
                ...result.work_rules!,
                legal_rules_source: text
            }));
        } catch (err: any) {
            console.error("Failed to parse rules:", err);
            setParseError(t("settingsPage.company.schedulingRules.parseFailed"));
        } finally {
            setParsing(false);
        }
    };

    // Update a single hard-constraint number field on the draft.
    // For optional fields, an empty string clears the value (becomes null).
    const updateDraftNumber = (
        field: keyof WorkRules,
        rawValue: string,
        opts?: { allowNull?: boolean; integer?: boolean }
    ) => {
        setWorkRulesDraft(prev => {
            if (rawValue.trim() === "") {
                if (opts?.allowNull) {
                    return { ...prev, [field]: null } as WorkRules;
                }
                return prev;  // refuse to clear required fields
            }
            const parsed = opts?.integer ? parseInt(rawValue, 10) : parseFloat(rawValue);
            if (Number.isNaN(parsed) || parsed < 0) return prev;
            return { ...prev, [field]: parsed } as WorkRules;
        });
    };

    const updateDraftWorkHour = (which: "default_start" | "default_end", value: string) => {
        setWorkRulesDraft(prev => ({
            ...prev,
            work_hours: { ...prev.work_hours, [which]: value }
        }));
    };

    const toggleArrayItem = (field: "department" | "languages" | "skills", item: string) => {
        const currentArray = profileDraft[field] || profile?.[field] || [];
        const newArray = currentArray.includes(item) ? currentArray.filter(i => i !== item) : [...currentArray, item];
        setProfileDraft(prev => ({ ...prev, [field]: newArray }));
    };

    const loadSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log("Loading memory settings...");
            const idToken = await getIdToken();
            console.log("Got ID token:", idToken ? "Token available" : "No token");
            const memorySettings = await getMemorySettings(idToken);
            console.log("Memory settings loaded:", memorySettings);
            setSettings(memorySettings);
        } catch (err) {
            console.error("Failed to load memory settings:", err);
            setError(t("settingsPage.errors.loadFailed"));
            // Set default values on error
            setSettings({
                memory_enabled: true,
                personalization_enabled: true,
                auto_learn_enabled: true,
                privacy_level: "medium",
                updated_at: new Date().toISOString()
            });
        } finally {
            setLoading(false);
        }
    };

    const updateSetting = async (key: keyof MemorySettings, value: any) => {
        if (!settings) return;

        try {
            console.log(`Updating setting ${key} to:`, value);
            const idToken = await getIdToken();
            console.log("Got ID token for update:", idToken ? "Token available" : "No token");
            const updatedSettings = await updateMemorySettings({ [key]: value }, idToken);
            console.log("Settings updated:", updatedSettings);
            setSettings(updatedSettings);
        } catch (err) {
            console.error("Failed to update setting:", err);
            setError(t("settingsPage.errors.updateFailed"));
        }
    };

    // Reusable toggle-row for an enableable hard constraint. The toggle
    // gates whether the validator and scheduler prompt apply this rule.
    // When disabled, the input is greyed out (still editable so the value
    // is preserved if the admin re-enables).
    const renderToggleableRule = (opts: {
        labelKey: string;
        enabledField: "rest_between_shifts_enabled" | "max_continuous_work_enabled" | "weekly_continuous_rest_enabled";
        valueField: "rest_between_shifts_hours" | "max_continuous_work_hours" | "min_weekly_continuous_rest_hours";
        showDraft: boolean;
        draft: WorkRules;
        view: WorkRules;
        suffix?: string;
        step?: string;
    }) => {
        const enabled = opts.showDraft ? opts.draft[opts.enabledField] : opts.view[opts.enabledField];
        const value = opts.showDraft ? opts.draft[opts.valueField] : opts.view[opts.valueField];
        return (
            <div className={`${styles.ruleRow} ${enabled ? "" : styles.ruleRowDisabled}`}>
                <Toggle
                    checked={enabled}
                    disabled={!opts.showDraft || savingWorkRules}
                    onChange={(_, checked) =>
                        setWorkRulesDraft(prev => ({ ...prev, [opts.enabledField]: !!checked }))
                    }
                    className={styles.ruleToggle}
                />
                <div className={styles.ruleBody}>
                    <label className={styles.ruleLabel}>
                        {t(opts.labelKey)}
                    </label>
                    <div className={styles.ruleControl}>
                        {opts.showDraft ? (
                            <input
                                type="number"
                                step={opts.step || "0.5"}
                                min="0"
                                className={styles.textInput}
                                value={value as number}
                                disabled={!enabled}
                                onChange={e => updateDraftNumber(opts.valueField, e.target.value)}
                            />
                        ) : (
                            <div className={styles.accountValue}>
                                {enabled ? `${value}${opts.suffix || "h"}` : t("settingsPage.company.schedulingRules.disabled")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderMembersSection = () => (
        <div className={styles.accountSection}>
            <h4 className={styles.accountSectionTitle}>{t("settingsPage.company.members.title")}</h4>
            <p className={styles.sharedDocsDescription}>{t("settingsPage.company.members.description")}</p>

            {membersError && <div className={styles.errorState}>{membersError}</div>}

            <div className={styles.addMemberRow}>
                <input
                    type="email"
                    className={styles.textInput}
                    value={newMemberEmail}
                    placeholder={t("settingsPage.company.members.emailPlaceholder")}
                    disabled={addingMember}
                    onChange={e => setNewMemberEmail(e.target.value)}
                />
                <select
                    className={styles.settingSelect}
                    value={newMemberRole}
                    disabled={addingMember}
                    onChange={e => setNewMemberRole(e.target.value as "admin" | "member")}
                >
                    <option value="member">{t("settingsPage.account.roleMember")}</option>
                    <option value="admin">{t("settingsPage.account.roleAdmin")}</option>
                </select>
                <button className={styles.saveButton} onClick={handleAddMember} disabled={addingMember || !newMemberEmail.trim()}>
                    {addingMember ? t("settingsPage.company.members.adding") : t("settingsPage.company.members.addButton")}
                </button>
            </div>

            {members.length === 0 ? (
                <div className={styles.sharedDocsEmpty}>{t("settingsPage.company.members.empty")}</div>
            ) : (
                <div className={styles.sharedDocsList}>
                    {members.map(member => {
                        const isSelf = member.email === currentUserEmail;
                        return (
                            <div className={styles.sharedDocItem} key={member.id}>
                                <div className={styles.sharedDocMeta}>
                                    <div className={styles.sharedDocName}>{member.display_name || member.email}</div>
                                    <div className={styles.sharedDocDetails}>{member.email}</div>
                                </div>
                                <select
                                    className={styles.settingSelect}
                                    value={member.role}
                                    disabled={isSelf || updatingMemberEmail === member.email}
                                    title={isSelf ? t("settingsPage.company.members.cannotChangeSelf") : undefined}
                                    onChange={e => handleMemberRoleChange(member, e.target.value as "admin" | "member")}
                                >
                                    <option value="member">{t("settingsPage.account.roleMember")}</option>
                                    <option value="admin">{t("settingsPage.account.roleAdmin")}</option>
                                </select>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const renderSchedulingRulesSection = () => {
        const view = workRules ?? DEFAULT_WORK_RULES;
        const draft = workRulesDraft;
        const showDraft = editingWorkRules;
        const updatedAt = formatCompanyProfileUpdated(view.updated_at);
        const updatedBy = view.updated_by;
        const sourceForCount = showDraft ? draft : view;
        const enabledCount =
            (sourceForCount.rest_between_shifts_enabled ? 1 : 0) +
            (sourceForCount.max_continuous_work_enabled ? 1 : 0) +
            (sourceForCount.weekly_continuous_rest_enabled ? 1 : 0);

        return (
            <div className={styles.accountSection}>
                <details className={styles.schedulingRulesDisclosure}>
                    <summary className={styles.schedulingRulesSummary}>
                        <ChevronRight size={16} className={styles.disclosureChevron} />
                        <ShieldCheck size={18} className={styles.accountSectionIcon} />
                        <span className={styles.schedulingRulesSummaryTitle}>
                            {t("settingsPage.company.schedulingRules.title")}
                        </span>
                        <span className={styles.schedulingRulesSummaryMeta}>
                            {t("settingsPage.company.schedulingRules.enabledCount", { n: enabledCount })}
                        </span>
                    </summary>

                    <div className={styles.schedulingRulesBody}>
                        <div className={styles.schedulingRulesActions}>
                            {!editingWorkRules ? (
                                <button className={styles.editButton} onClick={startEditingWorkRules}>
                                    <Pencil size={16} />
                                    {t("settingsPage.account.edit")}
                                </button>
                            ) : (
                                <div className={styles.editActions}>
                                    <button
                                        className={styles.cancelButton}
                                        onClick={cancelEditingWorkRules}
                                        disabled={savingWorkRules}
                                    >
                                        <X size={16} />
                                        {t("settingsPage.account.cancel")}
                                    </button>
                                    <button
                                        className={styles.saveButton}
                                        onClick={saveWorkRules}
                                        disabled={savingWorkRules}
                                    >
                                        <Check size={16} />
                                        {savingWorkRules
                                            ? t("settingsPage.account.saving")
                                            : t("settingsPage.account.save")}
                                    </button>
                                </div>
                            )}
                        </div>

                        <p className={styles.sharedDocsDescription}>
                            {t("settingsPage.company.schedulingRules.description")}
                        </p>

                        {workRulesError && <div className={styles.errorState}>{workRulesError}</div>}
                        {workRulesLoading && !workRules && (
                            <div className={styles.loadingState}>{t("settingsPage.loading")}</div>
                        )}

                        {/* Natural-language policy → LLM parse. Assist only:
                            failure keeps the text + the form below stays usable. */}
                        {showDraft && (
                            <div className={styles.rulesParseBlock}>
                                <label className={styles.accountLabel}>
                                    {t("settingsPage.company.schedulingRules.describeLabel")}
                                </label>
                                <textarea
                                    className={styles.companyProfileTextarea}
                                    style={{ minHeight: 120 }}
                                    value={rulesSourceText}
                                    placeholder={t("settingsPage.company.schedulingRules.describePlaceholder")}
                                    disabled={parsing || savingWorkRules}
                                    onChange={e => setRulesSourceText(e.target.value)}
                                />
                                {parseError && <div className={styles.errorState}>{parseError}</div>}
                                <div className={styles.rulesParseActions}>
                                    <button
                                        className={styles.saveButton}
                                        onClick={parseRulesText}
                                        disabled={parsing || savingWorkRules || !rulesSourceText.trim()}
                                    >
                                        <Sparkles size={16} />
                                        {parsing
                                            ? t("settingsPage.company.schedulingRules.parsing")
                                            : t("settingsPage.company.schedulingRules.parseButton")}
                                    </button>
                                    <span className={styles.rulesParseHint}>
                                        {t("settingsPage.company.schedulingRules.parseHint")}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* V1 hard rules with toggle */}
                        <div className={styles.ruleList}>
                            {renderToggleableRule({
                                labelKey: "settingsPage.company.schedulingRules.fields.restBetweenShifts",
                                enabledField: "rest_between_shifts_enabled",
                                valueField: "rest_between_shifts_hours",
                                showDraft,
                                draft,
                                view
                            })}
                            {renderToggleableRule({
                                labelKey: "settingsPage.company.schedulingRules.fields.maxContinuousWork",
                                enabledField: "max_continuous_work_enabled",
                                valueField: "max_continuous_work_hours",
                                showDraft,
                                draft,
                                view
                            })}
                            {renderToggleableRule({
                                labelKey: "settingsPage.company.schedulingRules.fields.weeklyRest",
                                enabledField: "weekly_continuous_rest_enabled",
                                valueField: "min_weekly_continuous_rest_hours",
                                showDraft,
                                draft,
                                view,
                                step: "1"
                            })}
                        </div>

                        {/* Other fields (no toggle — these are parameters or open-ended) */}
                        <div className={styles.schedulingRulesGrid}>
                            <div className={styles.accountItem}>
                                <label className={styles.accountLabel}>
                                    {t("settingsPage.company.schedulingRules.fields.minShiftForRest")}
                                </label>
                                {showDraft ? (
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        className={styles.textInput}
                                        value={draft.min_shift_duration_for_rest_rule_hours}
                                        onChange={e =>
                                            updateDraftNumber("min_shift_duration_for_rest_rule_hours", e.target.value)
                                        }
                                    />
                                ) : (
                                    <div className={styles.accountValue}>
                                        {view.min_shift_duration_for_rest_rule_hours}h
                                    </div>
                                )}
                            </div>
                            <div className={styles.accountItem}>
                                <label className={styles.accountLabel}>
                                    {t("settingsPage.company.schedulingRules.fields.workdayStart")}
                                </label>
                                {showDraft ? (
                                    <input
                                        type="time"
                                        className={styles.textInput}
                                        value={draft.work_hours.default_start}
                                        onChange={e => updateDraftWorkHour("default_start", e.target.value)}
                                    />
                                ) : (
                                    <div className={styles.accountValue}>{view.work_hours.default_start}</div>
                                )}
                            </div>
                            <div className={styles.accountItem}>
                                <label className={styles.accountLabel}>
                                    {t("settingsPage.company.schedulingRules.fields.workdayEnd")}
                                </label>
                                {showDraft ? (
                                    <input
                                        type="time"
                                        className={styles.textInput}
                                        value={draft.work_hours.default_end}
                                        onChange={e => updateDraftWorkHour("default_end", e.target.value)}
                                    />
                                ) : (
                                    <div className={styles.accountValue}>{view.work_hours.default_end}</div>
                                )}
                            </div>
                            <div className={styles.accountItem}>
                                <label className={styles.accountLabel}>
                                    {t("settingsPage.company.schedulingRules.fields.maxWeeklyHours")}
                                </label>
                                {showDraft ? (
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        className={styles.textInput}
                                        placeholder={t("settingsPage.company.schedulingRules.notSpecified")}
                                        value={draft.max_weekly_hours ?? ""}
                                        onChange={e =>
                                            updateDraftNumber("max_weekly_hours", e.target.value, { allowNull: true })
                                        }
                                    />
                                ) : (
                                    <div className={styles.accountValue}>
                                        {view.max_weekly_hours !== null
                                            ? `${view.max_weekly_hours}h`
                                            : t("settingsPage.company.schedulingRules.notSpecified")}
                                    </div>
                                )}
                            </div>
                            <div className={styles.accountItem}>
                                <label className={styles.accountLabel}>
                                    {t("settingsPage.company.schedulingRules.fields.maxConsecutiveDays")}
                                </label>
                                {showDraft ? (
                                    <input
                                        type="number"
                                        step="1"
                                        min="0"
                                        className={styles.textInput}
                                        placeholder={t("settingsPage.company.schedulingRules.notSpecified")}
                                        value={draft.max_consecutive_work_days ?? ""}
                                        onChange={e =>
                                            updateDraftNumber("max_consecutive_work_days", e.target.value, {
                                                allowNull: true,
                                                integer: true
                                            })
                                        }
                                    />
                                ) : (
                                    <div className={styles.accountValue}>
                                        {view.max_consecutive_work_days !== null
                                            ? `${view.max_consecutive_work_days}`
                                            : t("settingsPage.company.schedulingRules.notSpecified")}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Soft preferences block */}
                        <div className={styles.schedulingRulesSubsection}>
                            <label className={styles.accountLabel}>
                                {t("settingsPage.company.schedulingRules.fields.softPreferences")}
                            </label>
                            {showDraft ? (
                                <textarea
                                    className={styles.companyProfileTextarea}
                                    style={{ minHeight: 80 }}
                                    value={draft.soft_preferences}
                                    placeholder={t("settingsPage.company.schedulingRules.softPreferencesPlaceholder")}
                                    onChange={e =>
                                        setWorkRulesDraft(prev => ({ ...prev, soft_preferences: e.target.value }))
                                    }
                                />
                            ) : view.soft_preferences ? (
                                <div className={styles.accountValue}>{view.soft_preferences}</div>
                            ) : (
                                <div className={styles.placeholderText}>
                                    {t("settingsPage.company.schedulingRules.notSpecified")}
                                </div>
                            )}
                        </div>

                        {/* Detected but not yet supported — display-only, greyed.
                            Driven by draft in edit mode (fresh parse), else stored. */}
                        {(showDraft ? draft.unsupported_rules : view.unsupported_rules).length > 0 && (
                            <div className={styles.schedulingRulesSubsection}>
                                <label className={styles.accountLabel}>
                                    <ShieldAlert size={14} className={styles.inlineIcon} />
                                    {t("settingsPage.company.schedulingRules.unsupportedTitle")}
                                </label>
                                <ul className={styles.unsupportedRulesList}>
                                    {(showDraft ? draft.unsupported_rules : view.unsupported_rules).map(
                                        (rule, idx) => (
                                            <li key={idx} className={styles.unsupportedRuleItem}>
                                                {rule}
                                            </li>
                                        )
                                    )}
                                </ul>
                            </div>
                        )}

                        {/* Transparency table (nested disclosure stays collapsed by default) */}
                        <details className={styles.supportedRulesDisclosure}>
                            <summary className={styles.supportedRulesSummary}>
                                <ChevronRight size={14} className={styles.disclosureChevron} />
                                {t("settingsPage.company.schedulingRules.transparencyToggle")}
                            </summary>
                            <div className={styles.supportedRulesGrid}>
                                <div>
                                    <h5 className={styles.supportedRulesColumnTitle}>
                                        <ShieldCheck size={14} className={styles.inlineIcon} />
                                        {t("settingsPage.company.schedulingRules.weEnforce")}
                                    </h5>
                                    <ul className={styles.supportedRulesList}>
                                        {SUPPORTED_RULES.filter(r => r.support === "v1").map(r => (
                                            <li key={r.key} className={styles.supportedRulesItem}>
                                                <span>{t(`settingsPage.company.rules.${r.key}.label`)}</span>
                                                <span className={`${styles.tierBadge} ${styles[`tier_${r.tier}`]}`}>
                                                    {t(`settingsPage.company.tiers.${r.tier}`)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h5 className={styles.supportedRulesColumnTitle}>
                                        <ShieldAlert size={14} className={styles.inlineIcon} />
                                        {t("settingsPage.company.schedulingRules.notYet")}
                                    </h5>
                                    <ul className={styles.supportedRulesList}>
                                        {SUPPORTED_RULES.filter(r => r.support === "later").map(r => (
                                            <li key={r.key} className={styles.supportedRulesItem}>
                                                <span>{t(`settingsPage.company.rules.${r.key}.label`)}</span>
                                                <span className={`${styles.tierBadge} ${styles.tier_later}`}>
                                                    {t(`settingsPage.company.tiers.later`)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </details>

                        {/* Footer metadata */}
                        <div className={styles.companyProfileMeta}>
                            <span>
                                {updatedAt
                                    ? t("settingsPage.company.schedulingRules.lastUpdated", {
                                          who: updatedBy || t("settingsPage.company.unknownEditor"),
                                          when: updatedAt
                                      })
                                    : t("settingsPage.company.schedulingRules.neverUpdated")}
                            </span>
                        </div>
                    </div>
                </details>
            </div>
        );
    };

    // Source of truth for the tab list: prefer the CompanyContext role
    // (loaded once at app start, ready instantly when the modal opens).
    // Fall back to the modal-local role only if context isn't ready yet —
    // avoids the "tab pops in 3 seconds late" flicker from the /auth/me
    // call that loadUserCompanyRole kicks off.
    const effectiveRole = roleReady ? companyRole : userCompanyRole;
    const isAdmin = effectiveRole === "admin" || isOrganizationAdmin;

    const tabs = [
        { id: "general" as SettingsTab, label: t("settingsPage.tabs.general"), icon: <User size={18} /> },
        { id: "personalization" as SettingsTab, label: t("settingsPage.tabs.personalization"), icon: <Palette size={18} /> },
        { id: "account" as SettingsTab, label: t("settingsPage.tabs.account"), icon: <Shield size={18} /> },
        { id: "privacy" as SettingsTab, label: t("settingsPage.tabs.privacy"), icon: <Bell size={18} /> },
        ...(isAdmin
            ? [{ id: "company" as SettingsTab, label: t("settingsPage.tabs.company"), icon: <Building2 size={18} /> }]
            : []),
        { id: "workflows" as const, label: t("workflowBuilder.libraryTitle", { defaultValue: "Workflows" }), icon: <Workflow size={18} /> }
    ];

    const renderContent = () => {
        switch (activeTab) {
            case "general":
                return (
                    <div className={styles.contentSection}>
                        <h3 className={styles.sectionTitle}>{t("settingsPage.general.title")}</h3>

                        {/* Memory Section - ChatGPT Style */}
                        <div className={styles.memorySection}>
                            <h4 className={styles.memorySectionTitle}>{t("settingsPage.memory.title")}</h4>

                            {settings && (
                                <>
                                    <div className={styles.memoryOption}>
                                        <div className={styles.memoryOptionContent}>
                                            <div className={styles.memoryOptionTitle}>{t("settingsPage.memory.enable")}</div>
                                            <div className={styles.memoryOptionDescription}>{t("settingsPage.memory.enableDescription")}</div>
                                        </div>
                                        <Toggle
                                            checked={settings.memory_enabled}
                                            onChange={(_, checked) => updateSetting("memory_enabled", !!checked)}
                                            onText={t("settingsPage.memory.on")}
                                            offText={t("settingsPage.memory.off")}
                                            className={styles.memoryToggle}
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className={styles.memoryOption}>
                                        <div className={styles.memoryOptionContent}>
                                            <div className={styles.memoryOptionTitle}>{t("settingsPage.memory.personalize")}</div>
                                            <div className={styles.memoryOptionDescription}>{t("settingsPage.memory.personalizeDescription")}</div>
                                        </div>
                                        <Toggle
                                            checked={settings.personalization_enabled}
                                            onChange={(_, checked) => updateSetting("personalization_enabled", !!checked)}
                                            onText={t("settingsPage.memory.on")}
                                            offText={t("settingsPage.memory.off")}
                                            className={styles.memoryToggle}
                                            disabled={loading || !settings.memory_enabled}
                                        />
                                    </div>
                                </>
                            )}

                            {loading && <div className={styles.loadingState}>{t("settingsPage.loading")}</div>}

                            {error && <div className={styles.errorState}>{error}</div>}
                        </div>

                        <div className={styles.settingItem}>
                            <label className={styles.settingLabel}>{t("theme.label")}</label>
                            <div className={styles.themeSelector}>
                                <button className={styles.themeSelectorButton} onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}>
                                    {theme === "light" && <Sun size={18} />}
                                    {theme === "dark" && <Moon size={18} />}
                                    {theme === "system" && <Monitor size={18} />}
                                    <span>
                                        {theme === "light" && t("theme.light")}
                                        {theme === "dark" && t("theme.dark")}
                                        {theme === "system" && t("theme.system")}
                                    </span>
                                    <ChevronDown size={16} className={`${styles.chevron} ${themeDropdownOpen ? styles.chevronOpen : ""}`} />
                                </button>
                                {themeDropdownOpen && (
                                    <div className={styles.themeDropdown}>
                                        <button
                                            className={`${styles.themeOption} ${theme === "light" ? styles.themeOptionActive : ""}`}
                                            onClick={() => {
                                                setTheme("light");
                                                setThemeDropdownOpen(false);
                                            }}
                                        >
                                            <Sun size={18} />
                                            <span>{t("theme.light")}</span>
                                        </button>
                                        <button
                                            className={`${styles.themeOption} ${theme === "dark" ? styles.themeOptionActive : ""}`}
                                            onClick={() => {
                                                setTheme("dark");
                                                setThemeDropdownOpen(false);
                                            }}
                                        >
                                            <Moon size={18} />
                                            <span>{t("theme.dark")}</span>
                                        </button>
                                        <button
                                            className={`${styles.themeOption} ${theme === "system" ? styles.themeOptionActive : ""}`}
                                            onClick={() => {
                                                setTheme("system");
                                                setThemeDropdownOpen(false);
                                            }}
                                        >
                                            <Monitor size={18} />
                                            <span>{t("theme.system")}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <label className={styles.settingLabel}>{t("settingsPage.general.language")}</label>
                            <select className={styles.settingSelect}>
                                <option>English</option>
                                <option>Svenska</option>
                                <option>Deutsch</option>
                            </select>
                        </div>
                    </div>
                );

            case "personalization":
                return (
                    <div className={styles.contentSection}>
                        <h3 className={styles.sectionTitle}>{t("settingsPage.personalization.title")}</h3>
                        <p className={styles.placeholderText}>{t("settingsPage.personalization.placeholder")}</p>
                    </div>
                );

            case "account":
                return (
                    <div className={styles.contentSection}>
                        <div className={styles.accountHeaderRow}>
                            <h3 className={styles.sectionTitle}>{t("settingsPage.account.title")}</h3>
                            {!editingProfile ? (
                                <button className={styles.editButton} onClick={startEditingProfile}>
                                    <Pencil size={16} />
                                    {t("settingsPage.account.edit")}
                                </button>
                            ) : (
                                <div className={styles.editActions}>
                                    <button className={styles.cancelButton} onClick={cancelEditingProfile} disabled={savingProfile}>
                                        <X size={16} />
                                        {t("settingsPage.account.cancel")}
                                    </button>
                                    <button className={styles.saveButton} onClick={saveProfile} disabled={savingProfile}>
                                        <Check size={16} />
                                        {savingProfile ? t("settingsPage.account.saving") : t("settingsPage.account.save")}
                                    </button>
                                </div>
                            )}
                        </div>

                        {profileError && <div className={styles.errorState}>{profileError}</div>}

                        {/* Profile Photo Section */}
                        <div className={styles.profilePhotoSection}>
                            <div className={styles.profilePhotoWrapper}>
                                {profile?.profile_photo_url ? (
                                    <img src={profile.profile_photo_url} alt="Profile" className={styles.profilePhoto} />
                                ) : (
                                    <div className={styles.profilePhotoPlaceholder}>
                                        <User size={40} />
                                    </div>
                                )}
                                <button className={styles.photoUploadButton} onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}>
                                    <Camera size={16} />
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    onChange={handlePhotoUpload}
                                    style={{ display: "none" }}
                                />
                            </div>
                            {uploadingPhoto && <div className={styles.uploadingText}>{t("settingsPage.account.uploading")}</div>}
                        </div>

                        {/* Basic Info (Admin only editable) */}
                        <div className={styles.accountSection}>
                            <h4 className={styles.accountSectionTitle}>{t("settingsPage.account.basicInfo")}</h4>

                            <div className={styles.accountGrid}>
                                <div className={styles.accountItem}>
                                    <label className={styles.accountLabel}>{t("settingsPage.account.firstName")}</label>
                                    {editingProfile ? (
                                        <input
                                            type="text"
                                            className={styles.textInput}
                                            value={profileDraft.first_name ?? profile?.first_name ?? ""}
                                            onChange={e => setProfileDraft(prev => ({ ...prev, first_name: e.target.value }))}
                                            placeholder={t("settingsPage.account.firstName")}
                                        />
                                    ) : (
                                        <div className={styles.accountValue}>{profile?.first_name || "-"}</div>
                                    )}
                                </div>

                                <div className={styles.accountItem}>
                                    <label className={styles.accountLabel}>{t("settingsPage.account.lastName")}</label>
                                    {editingProfile ? (
                                        <input
                                            type="text"
                                            className={styles.textInput}
                                            value={profileDraft.last_name ?? profile?.last_name ?? ""}
                                            onChange={e => setProfileDraft(prev => ({ ...prev, last_name: e.target.value }))}
                                            placeholder={t("settingsPage.account.lastName")}
                                        />
                                    ) : (
                                        <div className={styles.accountValue}>{profile?.last_name || "-"}</div>
                                    )}
                                </div>

                                <div className={styles.accountItem}>
                                    <label className={styles.accountLabel}>{t("settingsPage.account.email")}</label>
                                    <div className={styles.accountValue}>{userInfo.email || "-"}</div>
                                </div>

                                <div className={styles.accountItem}>
                                    <label className={styles.accountLabel}>{t("settingsPage.account.employmentType")}</label>
                                    <div className={styles.accountValue}>
                                        {EMPLOYMENT_TYPE_OPTIONS.find(o => o.value === profile?.employment_type)?.label || "-"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Company Information */}
                        {(company || companyLoading) && (
                            <div className={styles.accountSection}>
                                <h4 className={styles.accountSectionTitle}>
                                    <Building2 size={18} className={styles.accountSectionIcon} />
                                    {t("settingsPage.account.companyInfo")}
                                </h4>

                                {companyLoading ? (
                                    <div className={styles.loadingState}>{t("settingsPage.loading")}</div>
                                ) : company ? (
                                    <div className={styles.accountGrid}>
                                        <div className={styles.accountItem}>
                                            <label className={styles.accountLabel}>{t("settingsPage.account.companyName")}</label>
                                            <div className={styles.accountValue}>{company.name}</div>
                                        </div>

                                        {userCompanyRole && (
                                            <div className={styles.accountItem}>
                                                <label className={styles.accountLabel}>{t("settingsPage.account.companyRole")}</label>
                                                <div className={styles.accountValue}>
                                                    {userCompanyRole === "admin" ? t("settingsPage.account.roleAdmin") : t("settingsPage.account.roleMember")}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {isAdmin && (
                            <div className={styles.accountSection}>
                                <h4 className={styles.accountSectionTitle}>{t("settingsPage.account.sharedDocuments.title")}</h4>
                                <p className={styles.sharedDocsDescription}>{t("settingsPage.account.sharedDocuments.description")}</p>

                                <div className={styles.sharedDocsActions}>
                                    <button
                                        className={styles.uploadSharedDocButton}
                                        onClick={() => sharedDocInputRef.current?.click()}
                                        disabled={uploadingCompanyDoc}
                                    >
                                        {uploadingCompanyDoc
                                            ? t("settingsPage.account.sharedDocuments.uploadingButton")
                                            : t("settingsPage.account.sharedDocuments.uploadButton")}
                                    </button>
                                    <input
                                        ref={sharedDocInputRef}
                                        type="file"
                                        accept=".txt,.md,.json,.csv,.png,.jpg,.jpeg,.bmp,.heic,.tiff,.pdf,.doc,.docx,.xlsx,.pptx,.html"
                                        onChange={handleCompanyDocUpload}
                                        style={{ display: "none" }}
                                    />
                                </div>

                                {companyDocsError && <div className={styles.errorState}>{companyDocsError}</div>}
                                {companyDocsLoading ? (
                                    <div className={styles.loadingState}>{t("settingsPage.loading")}</div>
                                ) : companySharedDocuments.length === 0 ? (
                                    <div className={styles.sharedDocsEmpty}>{t("settingsPage.account.sharedDocuments.empty")}</div>
                                ) : (
                                    <div className={styles.sharedDocsList}>
                                        {companySharedDocuments.map(doc => (
                                            <div className={styles.sharedDocItem} key={doc.filename}>
                                                <div className={styles.sharedDocMeta}>
                                                    <div className={styles.sharedDocName}>{doc.filename}</div>
                                                    <div className={styles.sharedDocDetails}>
                                                        {t("settingsPage.account.sharedDocuments.size")}: {formatDocumentSize(doc.size)} ·{" "}
                                                        {t("settingsPage.account.sharedDocuments.uploadedAt")}: {formatUploadedAt(doc.uploaded_at)}
                                                    </div>
                                                </div>
                                                <button
                                                    className={styles.deleteDocButton}
                                                    onClick={() => handleCompanyDocDelete(doc.filename)}
                                                    disabled={deletingCompanyDoc === doc.filename}
                                                >
                                                    {deletingCompanyDoc === doc.filename
                                                        ? t("settingsPage.account.sharedDocuments.deleting")
                                                        : t("settingsPage.account.sharedDocuments.delete")}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* User Editable Fields */}
                        <div className={styles.accountSection}>
                            <h4 className={styles.accountSectionTitle}>{t("settingsPage.account.workPreferences")}</h4>

                            {/* Phone */}
                            <div className={styles.accountItem}>
                                <label className={styles.accountLabel}>{t("settingsPage.account.phone")}</label>
                                {editingProfile ? (
                                    <input
                                        type="tel"
                                        className={styles.textInput}
                                        value={profileDraft.phone || ""}
                                        onChange={e => setProfileDraft(prev => ({ ...prev, phone: e.target.value }))}
                                        placeholder={t("settingsPage.account.phonePlaceholder")}
                                    />
                                ) : (
                                    <div className={styles.accountValue}>{profile?.phone || "-"}</div>
                                )}
                            </div>

                            {/* Department */}
                            <div className={styles.multiSelectSection}>
                                <label className={styles.accountLabel}>{t("settingsPage.account.department")}</label>
                                <div className={styles.chipContainer}>
                                    {DEPARTMENT_OPTIONS.map(dept => {
                                        const isSelected = editingProfile
                                            ? (profileDraft.department || profile?.department || []).includes(dept)
                                            : (profile?.department || []).includes(dept);
                                        return (
                                            <button
                                                key={dept}
                                                className={`${styles.chip} ${isSelected ? styles.chipSelected : ""}`}
                                                onClick={() => editingProfile && toggleArrayItem("department", dept)}
                                                disabled={!editingProfile}
                                            >
                                                {dept}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Languages */}
                            <div className={styles.multiSelectSection}>
                                <label className={styles.accountLabel}>{t("settingsPage.account.languages")}</label>
                                <div className={styles.chipContainer}>
                                    {LANGUAGE_OPTIONS.map(lang => {
                                        const isSelected = editingProfile
                                            ? (profileDraft.languages || profile?.languages || []).includes(lang)
                                            : (profile?.languages || []).includes(lang);
                                        return (
                                            <button
                                                key={lang}
                                                className={`${styles.chip} ${isSelected ? styles.chipSelected : ""}`}
                                                onClick={() => editingProfile && toggleArrayItem("languages", lang)}
                                                disabled={!editingProfile}
                                            >
                                                {lang}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Skills */}
                            <div className={styles.multiSelectSection}>
                                <label className={styles.accountLabel}>{t("settingsPage.account.skills")}</label>
                                <div className={styles.chipContainer}>
                                    {SKILLS_OPTIONS.map(skill => {
                                        const isSelected = editingProfile
                                            ? (profileDraft.skills || profile?.skills || []).includes(skill)
                                            : (profile?.skills || []).includes(skill);
                                        return (
                                            <button
                                                key={skill}
                                                className={`${styles.chip} ${isSelected ? styles.chipSelected : ""}`}
                                                onClick={() => editingProfile && toggleArrayItem("skills", skill)}
                                                disabled={!editingProfile}
                                            >
                                                {skill}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "privacy":
                return (
                    <div className={styles.contentSection}>
                        <h3 className={styles.sectionTitle}>{t("settingsPage.privacy.title")}</h3>
                        <p className={styles.placeholderText}>{t("settingsPage.privacy.placeholder")}</p>
                    </div>
                );

            case "company": {
                if (!isAdmin) return null;
                const currentDescription = company?.profile_description ?? "";
                const draftValue = editingCompanyProfile ? companyProfileDraft : currentDescription;
                const updatedAt = formatCompanyProfileUpdated(company?.profile_updated_at);
                const updatedBy = company?.profile_updated_by;

                return (
                    <div className={styles.contentSection}>
                        <div className={styles.accountHeaderRow}>
                            <h3 className={styles.sectionTitle}>{t("settingsPage.company.title")}</h3>
                            {!editingCompanyProfile ? (
                                <button className={styles.editButton} onClick={startEditingCompanyProfile}>
                                    <Pencil size={16} />
                                    {t("settingsPage.account.edit")}
                                </button>
                            ) : (
                                <div className={styles.editActions}>
                                    <button
                                        className={styles.cancelButton}
                                        onClick={cancelEditingCompanyProfile}
                                        disabled={savingCompanyProfile}
                                    >
                                        <X size={16} />
                                        {t("settingsPage.account.cancel")}
                                    </button>
                                    <button
                                        className={styles.saveButton}
                                        onClick={saveCompanyProfile}
                                        disabled={savingCompanyProfile}
                                    >
                                        <Check size={16} />
                                        {savingCompanyProfile
                                            ? t("settingsPage.account.saving")
                                            : t("settingsPage.account.save")}
                                    </button>
                                </div>
                            )}
                        </div>

                        <p className={styles.sharedDocsDescription}>{t("settingsPage.company.description")}</p>

                        {companyProfileError && <div className={styles.errorState}>{companyProfileError}</div>}

                        <div className={styles.accountSection}>
                            <textarea
                                className={styles.companyProfileTextarea}
                                value={draftValue}
                                placeholder={t("settingsPage.company.placeholder")}
                                maxLength={COMPANY_PROFILE_MAX_LEN}
                                disabled={!editingCompanyProfile || savingCompanyProfile}
                                onChange={e => setCompanyProfileDraft(e.target.value)}
                            />
                            <div className={styles.companyProfileMeta}>
                                <span>
                                    {updatedAt
                                        ? t("settingsPage.company.lastUpdated", {
                                              who: updatedBy || t("settingsPage.company.unknownEditor"),
                                              when: updatedAt
                                          })
                                        : t("settingsPage.company.neverUpdated")}
                                </span>
                                <span>
                                    {draftValue.length}/{COMPANY_PROFILE_MAX_LEN}
                                </span>
                            </div>
                        </div>

                        {renderMembersSection()}
                        {renderSchedulingRulesSection()}
                        <ReportThemeSettings getIdToken={getIdToken} />
                        {/* Note Workflows is the ONE place an organisation configures its
                            documentation. The documentation-type editor is an advanced,
                            optional surface: without any published type the backend runs the
                            flow on a neutral default definition. */}
                        <NoteWorkflowsEditor onNodeDialogOpenChange={setNoteWorkflowNodeDialogOpen} />
                        {company?.id && showAdvancedDocumentationTypes ? <DocumentationDefinitionAdmin organizationId={company.id} /> : null}
                        <OrgStructureEditor onNodeDialogOpenChange={setOrgStructureNodeDialogOpen} />
                        <AssessmentsEditor onDialogOpenChange={setAssessmentsDialogOpen} />
                        <FormTemplateManager />
                    </div>
                );
            }

            default:
                return null;
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen && !workflowWindowOpen}
                onDismiss={onDismiss}
                isBlocking={false}
                containerClassName={styles.modalContainer}
                focusTrapZoneProps={{ disabled: noteWorkflowNodeDialogOpen || orgStructureNodeDialogOpen || assessmentsDialogOpen }}
            >
                <div className={styles.settingsModal}>
                    {/* Header */}
                    <div className={styles.header}>
                        <h2 className={styles.headerTitle}>{t("settingsPage.title")}</h2>
                        <IconButton iconProps={{ iconName: "Cancel" }} ariaLabel={t("settingsPage.close")} onClick={onDismiss} className={styles.closeButton} />
                    </div>

                    <div className={styles.body}>
                        {/* Left Sidebar */}
                        <div className={styles.sidebar}>
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`${styles.sidebarItem} ${activeTab === tab.id ? styles.active : ""}`}
                                    aria-haspopup={tab.id === "workflows" ? "dialog" : undefined}
                                    onClick={() => {
                                        if (tab.id === "workflows") setWorkflowWindowOpen(true);
                                        else setActiveTab(tab.id);
                                    }}
                                >
                                    <span className={styles.sidebarIcon}>{tab.icon}</span>
                                    <span className={styles.sidebarLabel}>{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Right Content */}
                        <div className={styles.content}>{renderContent()}</div>
                    </div>
                </div>
            </Modal>
            {isOpen && workflowWindowOpen && <WorkflowBuilderWindow onClose={() => setWorkflowWindowOpen(false)} />}
        </>
    );
};
